import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { formatDateWithTime } from "Common/HelperClass";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement.js";
import ChipStatus from "Pages/GMS/ARS/Components/ChipStatus";
import { PivotSortableColumn } from "../constants/CSConstants";
import { ArsContext } from "Pages/GMS/ARS/Contexts/ArsContext";
import { formatTableData } from "../Helpers/CSHelper";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { IterationContext, IterationContextType } from "../contexts/IterationContext";
import Table from "Components/DesignSystem/Table/Table";
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from "Components/DesignSystem/Menu/Menu";
import Radio from "Components/DesignSystem/Radio/Radio";
import Tag from "Components/DesignSystem/Tag/Tag";
import { OverFlowIcon } from "assests/GmsIcons";
import { Edit, Delete, Copy, ChevronDown } from "Components/DesignSystem/Iconography/Icons";
import { TableColumn, SortDirection } from "types/DesignSystem/Table";
import { MenuItem } from "types/DesignSystem/Menu";

/**
 * One rendered grid row: a parent pivot or one of its expanded versions. The
 * versions render as SIBLING rows in the same columns (not a detail panel), so the
 * flattening — and the group zebra via `isRowSunken` (§ 240) — is what preserves
 * the legacy layout where a parent and its versions share one ground.
 */
type DisplayRow = {
    item: PivotBaseItem;
    /** The item itself, typed with `versions`/`expand` — set on PARENT rows only. */
    parentRow: PivotResponseItem | null;
    /** Set on version rows: the parent whose `uid` the default-swap flow needs. */
    parent: PivotResponseItem | null;
    defaultIndex: number;
    versionIndex: number | null;
};

/**
 * The Weightage Method list, on the design-system `Table` — same § 141 recipe as
 * `IterationTable`, plus § 240 (`isRowSunken`) for the parent+versions group zebra
 * and `sticky: 'left'` on the category column (the legacy hand-rolled pin painted
 * its own per-row grounds — the § 106 defect).
 *
 * Every behavioural gate is unchanged: `handleDefaultClick`/`getRadioCheckedValue`
 * (including the side-panel submodel-pivots path), the permission-gated menu
 * actions and their `{defaultIndex, versionIndex}` contract with the parent's
 * handlers, the versions-guard toast on parent Delete, and `toggleExpanded`.
 *
 * ⚠ The `.sort()` below mutates `pivot_attributes` IN PLACE — deliberately kept.
 * The parent (`ComputedSubstitutionView`) resolves menu actions by INDEXING into
 * its own `data.data.pivot_attributes`, which is this very array: the mutation is
 * what keeps those indices aligned with the rendered order. Sorting a copy here
 * would silently point Edit/Delete at the wrong row once the user sorts.
 */
function CSTable({ pivotData, setPivotData, loading, handleDelete, handleEditAndCreateVersion, NoDataComponent, handleDefaultVersionSelection, newlyCreatedId, isSidePanel, setRemoveDefaultId }: CSTableProps) {
    const [sortColumn, setSortColumn] = useState<PivotSortableColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [selectIndex, setSelectIndex] = useState<SelectIndex>({ defaultIndex: null, versionIndex: null });
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const { model, activeSubmodelIndex } = useContext(ArsContext);
    const { updatePermission, deletePermission, createPermission } = useContext<IterationContextType>(IterationContext);

    /* The menu is `tw-fixed` and the page scrolls — close it on scroll, as the legacy
       listener did. Outside-click and Escape are the DS Menu's `onDismiss`. */
    useEffect(() => {
        if (selectIndex.defaultIndex === null && selectIndex.versionIndex === null) return;
        const handleScroll = () => setSelectIndex({ defaultIndex: null, versionIndex: null });
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [selectIndex]);

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, defaultIndex: number, versionIndex: number | null) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
        setSelectIndex(prev => (
            prev.defaultIndex === defaultIndex && prev.versionIndex === versionIndex
                ? { defaultIndex: null, versionIndex: null }
                : { defaultIndex, versionIndex }
        ));
    };

    // In-place on purpose — see the docblock. Same comparator as always.
    const sortedData = pivotData?.data?.pivot_attributes?.sort((a, b) => {
        if (sortColumn) {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                return sortDirection === "asc"
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            }
        }
        return 0;
    });

    const formattedData = isSidePanel && model?.submodel[activeSubmodelIndex]?.pivots ? formatTableData(sortedData, model?.submodel[activeSubmodelIndex]?.pivots) : sortedData;

    const handleDefaultClick = (version: PivotVersion, removeDefault?: string | null) => {
        handleDefaultVersionSelection(version);
        if (isSidePanel && removeDefault) {
            setRemoveDefaultId(removeDefault);
        }
    };

    const handleSortChange = (columnId: string, direction: SortDirection) => {
        // Client-side sort still always carries a direction: fold the DS cycle's
        // null back to 'asc', keeping same-column clicks toggling as before.
        const next = direction ?? 'asc';
        setSortColumn(columnId as PivotSortableColumn);
        setSortDirection(next === 'desc' ? 'desc' : 'asc');
    };

    const deleteFunction = async (selectIndex: SelectIndex) => {
        handleDelete(selectIndex)
    }

    const toggleExpanded = (index: number) => {
        setPivotData(prevData => ({
            ...prevData,
            data: {
                ...prevData.data,
                pivot_attributes: prevData.data.pivot_attributes.map((item, i) =>
                    i === index ? { ...item, expand: !item.expand } : item
                )
            }
        }));
    };

    const getRadioCheckedValue = (responseItem: PivotBaseItem) => {
        const pivots = model?.submodel[activeSubmodelIndex]?.pivots;

        if (!pivots || Object.keys(pivots).length === 0) {
            return responseItem.default;
        }

        return pivots.includes(responseItem.uid);
    };

    const renderRowActions = (row: DisplayRow) => {
        const { item, parentRow, defaultIndex, versionIndex } = row;
        const isParent = versionIndex === null;
        const isOpen = selectIndex.defaultIndex === defaultIndex && selectIndex.versionIndex === versionIndex;
        const closeMenu = () => setSelectIndex({ defaultIndex: null, versionIndex: null });

        const menuItems: MenuItem[] = [
            {
                id: 'edit', label: 'Edit', icon: <Edit />,
                disabled: !updatePermission,
                'data-testid': `overflow-edit-${item.uid}`,
                onClick: () => { closeMenu(); handleEditAndCreateVersion({ defaultIndex, versionIndex }, isParent ? "edit" : "edit_version"); },
            },
            {
                id: 'create_version', label: 'Create Version', icon: <Copy />,
                disabled: !createPermission,
                'data-testid': `overflow-create-version-${item.uid}`,
                onClick: () => { closeMenu(); handleEditAndCreateVersion({ defaultIndex, versionIndex }, "create_version"); },
            },
            {
                id: 'delete', label: 'Delete', icon: <Delete />,
                disabled: !deletePermission,
                'data-testid': `overflow-delete-${item.uid}`,
                onClick: () => {
                    closeMenu();
                    if (isParent && parentRow?.versions && parentRow.versions.length > 0) {
                        toast.error("You can’t delete this substitution because it has versions")
                    } else {
                        deleteFunction({ defaultIndex, versionIndex });
                    }
                },
            },
        ];

        return (
            <>
                <button
                    type="button"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    data-testid={`cs-row-overflow-${item.uid}`}
                    className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                    /* Menu dismisses on document MOUSEDOWN — without this, pressing the
                       open trigger dismisses-then-reopens on the same click. */
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => handleOverflowClick(e, defaultIndex, versionIndex)}
                >
                    <OverFlowIcon />
                </button>
                {isOpen ? ReactDOM.createPortal(
                    <div
                        className="tw-fixed tw-z-[9999] tw-w-48"
                        style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                    >
                        <Menu
                            variant="compact"
                            items={menuItems}
                            data-testid={`cs-row-overflow-menu-${item.uid}`}
                            onDismiss={closeMenu}
                        />
                    </div>,
                    document.body
                ) : null}
            </>
        );
    };

    const columns: TableColumn<DisplayRow>[] = [
        {
            id: PivotSortableColumn.CATEGORY,
            header: 'Category',
            accessor: (r) => r.item?.category_name ?? '',
            sortable: true,
            sticky: 'left',
            render: (_value, row) => {
                const { item, parentRow, defaultIndex, versionIndex } = row;
                if (versionIndex !== null) {
                    // Version rows keep the indent slot but show no category.
                    return <div className="tw-h-6" />;
                }
                return (
                    <div className="tw-flex tw-items-center tw-justify-start">
                        <div className="tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center">
                            {parentRow && parentRow.versions?.length >= 1 ? (
                                <button
                                    type="button"
                                    aria-expanded={Boolean(parentRow.expand)}
                                    aria-label={parentRow.expand ? 'Collapse versions' : 'Expand versions'}
                                    data-testid={`cs-expand-${item.uid}`}
                                    className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-ctl tw-text-ink-secondary hover:tw-bg-surface-hover-strong"
                                    onClick={() => toggleExpanded(defaultIndex)}
                                >
                                    <span className={parentRow.expand ? 'tw-rotate-180 tw-flex' : 'tw-flex'}>
                                        <ChevronDown />
                                    </span>
                                </button>
                            ) : <div className="tw-w-6 tw-h-6" />}
                        </div>
                        <div className="tw-text-sm tw-leading-tight">{item?.category_name ?? ""}</div>
                    </div>
                );
            },
        },
        {
            id: PivotSortableColumn.VERSION,
            header: 'Identifier',
            accessor: (r) => r.item?.version_name ?? '',
            sortable: true,
            render: (_value, row) => (
                <div className="tw-text-sm tw-leading-tight tw-flex tw-items-center tw-gap-2">
                    {row.item.version_name}
                    {newlyCreatedId === row.item.uid ? <Tag tone="info" label="New" /> : null}
                </div>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            accessor: (r) => r.item?.status ?? '',
            render: (_value, row) => <ChipStatus status={row.item.status.toLowerCase() === "live"} />,
        },
        {
            id: PivotSortableColumn.ATTRIBUTES,
            header: 'Attributes',
            accessor: (r) => r.item?.attribute ?? '',
            sortable: true,
            render: (_value, row) => (
                <div className="tw-text-sm tw-leading-tight tw-overflow-hidden tw-truncate">{row.item.attribute}</div>
            ),
        },
        {
            id: PivotSortableColumn.CREATED_ON,
            header: 'Created on',
            accessor: (r) => formatDateWithTime(r.item?.created_on) || "",
            sortable: true,
            cellType: 'meta',
        },
        {
            id: PivotSortableColumn.LAST_UPDATED_BY,
            header: 'Last updated by',
            accessor: (r) => r.item?.last_updated_by?.username ?? '',
            sortable: true,
        },
        {
            id: 'default',
            header: 'Default',
            accessor: (r) => r.item?.default,
            render: (_value, row) => {
                const { item, parent, defaultIndex, versionIndex } = row;
                const isParent = versionIndex === null;
                return (
                    <Radio
                        label=""
                        aria-label="Set as default"
                        value={item.uid}
                        name={isParent ? `radio-group-${defaultIndex}` : `radio-group-${defaultIndex},${versionIndex}`}
                        checked={Boolean(!isSidePanel ? item.default : getRadioCheckedValue(item))}
                        disabled={isParent ? undefined : !updatePermission}
                        onChange={() => isParent ? handleDefaultClick(item) : handleDefaultClick(item, parent?.uid)}
                        data-testid={`cs-default-radio-${item.uid}`}
                    />
                );
            },
        },
        ...(!isSidePanel ? [{
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '48px',
            align: 'right',
            render: (_value: unknown, row: DisplayRow) => renderRowActions(row),
        } as TableColumn<DisplayRow>] : []),
    ];

    if (loading) { return <div className=""><TableSkeletonElement /></div> }

    if (loading === false && pivotData?.data?.pivot_attributes?.length === 0) { return <div><NoDataComponent /></div> }

    const displayRows: DisplayRow[] = (formattedData ?? []).flatMap((responseItem: PivotResponseItem, defaultIndex: number) => [
        { item: responseItem, parentRow: responseItem, parent: null, defaultIndex, versionIndex: null },
        ...(responseItem?.versions?.length >= 1 && responseItem?.expand
            ? responseItem.versions.map((version: PivotVersion, versionIndex: number) => ({
                item: version, parentRow: null, parent: responseItem, defaultIndex, versionIndex,
            }))
            : []),
    ]);

    return (
        <div className="tw-mt-3">
            <Table
                /* Bound the grid so <main> stops scrolling the page — see
                   `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                   needs it: sticky resolves against a scrolling ancestor. */
                stickyHeader
                maxHeight={PAGE_GRID_MAX_HEIGHT}
                columns={columns}
                data={displayRows}
                rowKey={(r) => r.item.uid}
                hoverable
                bordered
                isRowSunken={(r) => r.defaultIndex % 2 === 1}
                sortState={sortColumn ? { columnId: sortColumn, direction: sortDirection } : undefined}
                onSort={handleSortChange}
                id="cs-table"
                data-testid="cs-table"
            />
        </div>
    );
}

export default CSTable
